import { graphql } from 'relay-runtime';

import { fg } from '@confluence/feature-gating';
import { keys as sessionStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { type UseQueryInteropSupportedResult } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';

// eslint-disable-next-line import/no-cycle
import { PopularFeedTimeGranularityValue } from './Feed.tsx';
import { useFeedDropdownStorage } from './useFeedDropdownStorage.ts';
import { PopularFeedWithTimeGranularityStorageSnippetsQuery } from './PopularFeedWithTimeGranularityStorageSnippets.graphql.ts';
import type {
	PopularFeedWithTimeGranularityStorageSnippetsQuery$data,
	PopularFeedWithTimeGranularityStorageSnippetsQuery$variables,
} from './__generated__/PopularFeedWithTimeGranularityStorageSnippetsQuery.graphql.ts';
import type {
	usePopularFeedWithTimeGranularityStorageQuery$data,
	usePopularFeedWithTimeGranularityStorageQuery$variables,
} from './__generated__/usePopularFeedWithTimeGranularityStorageQuery.graphql.ts';

export const usePopularFeedWithTimeGranularityStorageQuery = graphql`
	query usePopularFeedWithTimeGranularityStorageQuery(
		$first: Int
		$after: String
		$timeGranularity: String = "default"
		$includeGeneratedBy: Boolean = false
	) {
		popularFeed(first: $first, after: $after, timeGranularity: $timeGranularity) {
			nodes {
				content {
					title
					id
					type
					subType
					emoji: properties(key: "emoji-title-published") {
						nodes {
							id
							value
						}
					}
					contentProperties @include(if: $includeGeneratedBy) {
						latest {
							generatedBy
						}
					}
					excerpt(length: 350)
					labels {
						nodes {
							name
						}
					}
					links {
						webui
					}
					space {
						alias
						id
						key
						name
					}
					history {
						createdDate
						createdBy {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
							... on UnknownUser {
								accountId
							}
						}
						ownedBy {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
						}
					}
					metadata {
						lastModifiedDate
						comments {
							commentsCount
						}
						currentuser {
							favourited {
								isFavourite
							}
						}
					}
					version {
						friendlyWhen
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

export const usePopularFeedWithTimeGranularityStorage = (
	renderPopularFeed: boolean,
): {
	popularFeedTimeGranularity: PopularFeedTimeGranularityValue;
	setPopularFeedTimeGranularity: (timeGranularity: PopularFeedTimeGranularityValue) => void;
	popularFeedQuery: UseQueryInteropSupportedResult<
		usePopularFeedWithTimeGranularityStorageQuery$data,
		usePopularFeedWithTimeGranularityStorageQuery$variables
	>;
} => {
	const { value, setValue, query } = useFeedDropdownStorage<
		usePopularFeedWithTimeGranularityStorageQuery$data,
		usePopularFeedWithTimeGranularityStorageQuery$variables,
		PopularFeedTimeGranularityValue
	>(
		{
			defaultValue: PopularFeedTimeGranularityValue.Default,
			sessionStorageKey: sessionStorageKeys.HOME_POPULAR_FEED_TIME_GRANULARITY,
			skipQuery:
				!renderPopularFeed ||
				UNSAFE_expValEqualsNoExposure(
					'confluence_ai_snippets_in_following_popular_feeds',
					'isEnabled',
					true,
				),
		},
		{
			query: usePopularFeedWithTimeGranularityStorageQuery,
			variables: (
				timeGranularity: PopularFeedTimeGranularityValue,
			): usePopularFeedWithTimeGranularityStorageQuery$variables => ({
				timeGranularity,
				includeGeneratedBy: fg('cc-activation-use-generated-by-content-property'),
			}),
		},
	);
	const {
		value: valueWithSnippets,
		setValue: setValueWithSnippets,
		query: queryWithSnippets,
	} = useFeedDropdownStorage<
		PopularFeedWithTimeGranularityStorageSnippetsQuery$data,
		PopularFeedWithTimeGranularityStorageSnippetsQuery$variables,
		PopularFeedTimeGranularityValue
	>(
		{
			defaultValue: PopularFeedTimeGranularityValue.Default,
			sessionStorageKey: sessionStorageKeys.HOME_POPULAR_FEED_TIME_GRANULARITY,
			skipQuery:
				!renderPopularFeed ||
				!UNSAFE_expValEqualsNoExposure(
					'confluence_ai_snippets_in_following_popular_feeds',
					'isEnabled',
					true,
				),
			client: getAGGClient(),
		},
		{
			query: PopularFeedWithTimeGranularityStorageSnippetsQuery,
			variables: (
				timeGranularity: PopularFeedTimeGranularityValue,
			): PopularFeedWithTimeGranularityStorageSnippetsQuery$variables => ({
				timeGranularity,
				includeGeneratedBy: fg('cc-activation-use-generated-by-content-property'),
			}),
		},
	);

	return {
		popularFeedTimeGranularity: UNSAFE_expValEqualsNoExposure(
			'confluence_ai_snippets_in_following_popular_feeds',
			'isEnabled',
			true,
		)
			? valueWithSnippets
			: value,
		setPopularFeedTimeGranularity: UNSAFE_expValEqualsNoExposure(
			'confluence_ai_snippets_in_following_popular_feeds',
			'isEnabled',
			true,
		)
			? setValueWithSnippets
			: setValue,
		popularFeedQuery: UNSAFE_expValEqualsNoExposure(
			'confluence_ai_snippets_in_following_popular_feeds',
			'isEnabled',
			true,
		)
			? queryWithSnippets
			: query,
	};
};
